import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  IconButton,
  Paper,
  Divider,
  TextField,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import Navbar from './common/Navbar';
import Footer from './common/Footer';
import Breadcrumb from './common/Breadcrumb';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { formatPrice } from '../utils/formatPrice';

const Cart = () => {
  const navigate = useNavigate();
  const { cartItems, updateQuantity, removeFromCart, getCartTotal } = useCart();
  const { isAuthenticated, openLoginModal } = useAuth();
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };
  useEffect(() => {
    scrollToTop();
  }, []);
  const subtotal = getCartTotal();
  const deliveryCharge = subtotal >= 499 ? 0 : 40;
  const total = subtotal + deliveryCharge;

  const handleCheckout = () => {
    if (!isAuthenticated) {
      openLoginModal();
      return;
    }
    navigate('/payment');
  };

  const CartItem = ({ item }) => (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 2,
        borderRadius: 2,
        border: '1px solid #eee',
      }}
    >
      <Grid container spacing={2} alignItems="center">
        {/* Product Image */}
        <Grid item xs={3} sm={2}>
          <Box
            onClick={() => navigate(`/viewproduct/${item._id}`)}
            sx={{
              cursor: 'pointer',
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: '#f5f5f5',
            }}
          >
            <img
              src={item.ProductMainImgUrl}
              alt={item.ProductName}
              style={{
                width: '100%',
                height: 80,
                objectFit: 'cover',
              }}
            />
          </Box>
        </Grid>

        {/* Product Details */}
        <Grid item xs={9} sm={4}>
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            onClick={() => navigate(`/viewproduct/${item._id}`)}
            sx={{
              cursor: 'pointer',
              '&:hover': { color: '#8B4513' },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {item.ProductName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Weight: {item.ProductSize || '250g'}
          </Typography>
        </Grid>

        {/* Quantity Controls */}
        <Grid item xs={6} sm={3}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              size="small"
              onClick={() => updateQuantity(item._id, item.ProductQuantity - 1)}
              sx={{ border: '1px solid #ddd' }}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
            <Typography
              sx={{
                px: 2,
                py: 0.5,
                border: '1px solid #ddd',
                borderRadius: 1,
                minWidth: 40,
                textAlign: 'center',
              }}
            >
              {item.ProductQuantity}
            </Typography>
            <IconButton
              size="small"
              onClick={() => updateQuantity(item._id, item.ProductQuantity + 1)}
              sx={{ border: '1px solid #ddd' }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Box>
        </Grid>

        {/* Price */}
        <Grid item xs={4} sm={2}>
          <Typography variant="h6" fontWeight="bold" color="#2e7d32">
            Rs.{formatPrice(item.ProductPrice * item.ProductQuantity)}
          </Typography>
          {item.ProductMRP > item.ProductPrice && (
            <Typography
              variant="body2"
              sx={{ textDecoration: 'line-through', color: '#999' }}
            >
              Rs.{formatPrice(item.ProductMRP * item.ProductQuantity)}
            </Typography>
          )}
        </Grid>

        {/* Remove Button */}
        <Grid item xs={2} sm={1} sx={{ textAlign: 'right' }}>
          <IconButton
            onClick={() => removeFromCart(item._id)}
            sx={{ color: '#e53935' }}
          >
            <DeleteOutlineIcon />
          </IconButton>
        </Grid>
      </Grid>
    </Paper>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#faf8f5' }}>
      <Navbar />

      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Breadcrumb items={[{ label: 'Shopping Cart' }]} />

        <Typography variant="h4" fontWeight="bold" color="#3E2723" sx={{ mb: 3 }}>
          Shopping Cart
          {cartItems.length > 0 && (
            <Typography component="span" color="text.secondary" sx={{ ml: 1, fontSize: '1rem' }}>
              ({cartItems.length} {cartItems.length === 1 ? 'item' : 'items'})
            </Typography>
          )}
        </Typography>

        {cartItems.length > 0 ? (
          <Grid container spacing={4}>
            {/* Cart Items */}
            <Grid item xs={12} md={8}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: '#fff' }}>
                {cartItems.map((item) => (
                  <CartItem key={item._id} item={item} />
                ))}

                <Button
                  onClick={() => navigate('/filter/All')}
                  sx={{ mt: 2, color: '#8B4513' }}
                >
                  Continue Shopping
                </Button>
              </Paper>
            </Grid>

            {/* Order Summary */}
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: '#fff' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Order Summary
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography color="text.secondary">Subtotal</Typography>
                  <Typography fontWeight="bold">Rs.{formatPrice(subtotal)}</Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography color="text.secondary">Delivery</Typography>
                  <Typography fontWeight="bold" color={deliveryCharge === 0 ? '#2e7d32' : 'inherit'}>
                    {deliveryCharge === 0 ? 'FREE' : `Rs.${deliveryCharge}`}
                  </Typography>
                </Box>

                {deliveryCharge > 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Add Rs.{formatPrice(499 - subtotal)} more for free delivery
                  </Typography>
                )}

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                  <Typography variant="h6" fontWeight="bold">Total</Typography>
                  <Typography variant="h6" fontWeight="bold" color="#2e7d32">
                    Rs.{formatPrice(total)}
                  </Typography>
                </Box>

                {/* Coupon Code */}
                <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                  <TextField
                    placeholder="Enter coupon code"
                    size="small"
                    fullWidth
                  />
                  <Button
                    variant="outlined"
                    sx={{ borderColor: '#8B4513', color: '#8B4513' }}
                  >
                    Apply
                  </Button>
                </Box>

                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={handleCheckout}
                  sx={{
                    py: 1.5,
                    bgcolor: '#8B4513',
                    '&:hover': { bgcolor: '#6d3610' },
                    fontWeight: 'bold',
                    fontSize: '1rem',
                  }}
                >
                  Proceed to Checkout
                </Button>

                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Secure checkout powered by trusted payment gateways
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        ) : (
          <Paper
            elevation={0}
            sx={{
              p: 6,
              borderRadius: 3,
              bgcolor: '#fff',
              textAlign: 'center',
            }}
          >
            <ShoppingBagIcon sx={{ fontSize: 80, color: '#ddd', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Your cart is empty
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Looks like you haven't added any products yet
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/filter/All')}
              sx={{
                bgcolor: '#8B4513',
                '&:hover': { bgcolor: '#6d3610' },
                px: 4,
              }}
            >
              Start Shopping
            </Button>
          </Paper>
        )}
      </Container>

      <Footer />
    </Box>
  );
};

export default Cart;
